/**
 * Vector tiles from a PMTiles archive in the bucket — the crowns and stems a step pre-cut beside
 * its GeoPackage — read with range requests over a signed link, so nothing is downloaded whole
 * and nothing parsed on the main thread. Served to MapLibre through our own protocol, which
 * consults the object's tile mask (tileMask.ts) first: tiles away from the picked units are
 * empty without a single request.
 */
import { PMTiles, type Header, type RangeResponse, type Source } from 'pmtiles';
import type { LinkSource } from './linkSource';
import type { Geometry } from 'geojson';
import { insideAny } from './geometry';
import { filterTile } from './tileFilter';
import type { FilterRequest, FilterResponse } from './tileFilter.worker';
import { crownTileMask, tileState } from './tileMask';

export const PMTILES_PROTOCOL = 'pmtiles-gs';

/** The source layers of the archives, by what they hold. */
export const PMTILES_LAYERS = {
  /** A photo inventory: one point per crown (thinned below z15, z10–15) and the polygons (z16–18). */
  photo: { points: 'trees', polygons: 'crowns' },
  /** An MLS analysis: the stems (z10–18) and the crowns (z14–18). */
  mls: { points: 'tree', polygons: 'crown' },
} as const;

/** The archive's name beside a GeoPackage, as the backend signs it. */
export const pmtilesPath = (gpkg: string) => gpkg.replace(/\.gpkg$/i, '') + '.pmtiles';

/** The tile URL template of an object's archive; the version makes MapLibre refetch when the mask changes. */
export const pmtilesTileUrl = (object: string, version = 0) => `${PMTILES_PROTOCOL}://${object}/{z}/{x}/{y}?v=${version}`;

/**
 * Range reads over a signed link. The link source renews a link shortly before it expires; a
 * read the bucket still refuses (403: expired after all) asks for a fresh link once and retries.
 */
export class SignedRangeSource implements Source {
  constructor(
    private readonly object: string,
    private readonly path: string,
    private readonly links: LinkSource,
    private readonly fetchImpl: typeof fetch = (...args) => fetch(...args),
  ) {}

  getKey(): string {
    return `${this.object}/${this.path}`;
  }

  async getBytes(offset: number, length: number, signal?: AbortSignal): Promise<RangeResponse> {
    for (let attempt = 0; ; attempt++) {
      const url = await this.links.url(this.object, this.path);
      const response = await this.fetchImpl(url, { headers: { Range: `bytes=${offset}-${offset + length - 1}` }, signal });
      if ((response.status === 403 || response.status === 400) && attempt === 0) {
        this.links.forget?.(this.object, this.path);
        continue;
      }
      if (!response.ok) throw new Error(`bucket ${response.status}`);
      const data = await response.arrayBuffer();
      // A server that ignores the range sends the whole file.
      if (response.status === 200 && data.byteLength > length) return { data: data.slice(offset, offset + length) };
      return { data };
    }
  }
}

export interface Archive {
  object: string;
  path: string;
  tiles: PMTiles;
  header: Header;
}

const archives = new Map<string, Promise<Archive | null>>();

/**
 * The archive beside an object's GeoPackage, opened once a session; null when there is none
 * (the backend will not sign it) or it cannot be read — the page then reads the GeoPackage.
 */
export function openPmtiles(object: string, gpkg: string, links: LinkSource, fetchImpl?: typeof fetch): Promise<Archive | null> {
  const path = pmtilesPath(gpkg);
  const key = `${object}\u0000${path}`;
  const held = archives.get(key);
  if (held) return held;
  const tiles = new PMTiles(new SignedRangeSource(object, path, links, fetchImpl));
  const opening = tiles.getHeader().then(
    (header) => ({ object, path, tiles, header }),
    () => null,
  );
  archives.set(key, opening);
  // The tile protocol finds an object's archive by the object alone.
  byObject.set(object, opening);
  return opening;
}

const byObject = new Map<string, Promise<Archive | null>>();

const TILE_MEMORY_MAX = 512;
const tileMemory = new Map<string, Promise<Uint8Array>>();

/** Forget the archives this session opened; for tests. */
export function forgetArchives(): void {
  archives.clear();
  byObject.clear();
  tileMemory.clear();
  warnedUnanchored.clear();
}

/** The tile for a `pmtiles-gs://<object>/<z>/<x>/<y>` url, as the object's mask wants it: empty
 * where it touches no picked unit, untouched where it lies inside one, and on a boundary cut to
 * the features anchored inside the picks (tileFilter.ts, in a worker). */
export async function servePmtilesTile(url: string, signal?: AbortSignal): Promise<Uint8Array> {
  const m = new RegExp(`^${PMTILES_PROTOCOL}://([^/]+)/(\\d+)/(\\d+)/(\\d+)`).exec(url);
  if (!m) return new Uint8Array(0);
  const [object, z, x, y] = [m[1]!, Number(m[2]), Number(m[3]), Number(m[4])];
  const state = tileState(object, z, x, y);
  if (state === 'out') return new Uint8Array(0);
  const archive = await byObject.get(object);
  if (!archive) return new Uint8Array(0);
  // Keyed by the archive too: one replaced in the bucket (another size) is never served from an old cut.
  const tag = `${object}:${archive.header.tileDataLength}`;
  const raw = remembered(`${tag}/${z}/${x}/${y}`, () => archive.tiles.getZxy(z, x, y, signal).then((t) => (t ? new Uint8Array(t.data) : new Uint8Array(0))));
  const mask = state === 'edge' ? crownTileMask(object) : null;
  if (!mask) return (await raw).slice();
  // Cut once per tile and mask: a later pick has a new version, and a new cut.
  const cut = remembered(`${tag}/${z}/${x}/${y}@${mask.version}`, async () => {
    const bytes = await raw;
    return bytes.length ? cutTile(object, mask, bytes, z, x, y) : bytes;
  });
  return (await cut).slice();
}

/** Tile bytes of this session, most recent last; a few hundred tiles of a few hundred kB at most. */
function remembered(key: string, load: () => Promise<Uint8Array>): Promise<Uint8Array> {
  const held = tileMemory.get(key);
  if (held) {
    tileMemory.delete(key);
    tileMemory.set(key, held);
    return held;
  }
  const loading = load();
  tileMemory.set(key, loading);
  loading.catch(() => tileMemory.delete(key));
  while (tileMemory.size > TILE_MEMORY_MAX) tileMemory.delete(tileMemory.keys().next().value!);
  return loading;
}

const warnedUnanchored = new Set<string>();
function noteUnanchored(object: string, count: number): void {
  if (!count || warnedUnanchored.has(object)) return;
  warnedUnanchored.add(object);
  console.info(`pmtiles: ${object} has features without an anchor (ax/ay); they are kept whatever the picks`);
}

let filterWorker: Worker | null = null;
const sentMasks = new Set<string>();
const pendingCuts = new Map<number, { resolve: (b: Uint8Array) => void; reject: (e: Error) => void; object: string }>();
let cutSeq = 0;

/** A boundary tile cut to the mask: in the filter worker where there is one, in the thread otherwise (tests). */
function cutTile(object: string, mask: { geometries: Geometry[]; version: number }, bytes: Uint8Array, z: number, x: number, y: number): Promise<Uint8Array> {
  if (typeof Worker === 'undefined') {
    const { bytes: out, unanchored } = filterTile(bytes, insideAny(mask.geometries.map((geometry) => ({ type: 'Feature', properties: {}, geometry }))), z, x, y);
    noteUnanchored(object, unanchored);
    return Promise.resolve(out);
  }
  if (!filterWorker) {
    filterWorker = new Worker(new URL('./tileFilter.worker.ts', import.meta.url), { type: 'module' });
    filterWorker.onmessage = (e: MessageEvent<FilterResponse>) => {
      const r = e.data;
      const waiting = pendingCuts.get(r.id);
      pendingCuts.delete(r.id);
      if (!waiting) return;
      if ('error' in r) waiting.reject(new Error(r.error));
      else {
        noteUnanchored(waiting.object, r.unanchored);
        waiting.resolve(r.bytes);
      }
    };
  }
  const key = `${object}#${mask.version}`;
  if (!sentMasks.has(key)) {
    sentMasks.add(key);
    filterWorker.postMessage({ kind: 'mask', key, geometries: mask.geometries } satisfies FilterRequest);
  }
  const id = ++cutSeq;
  const copy = bytes.slice();
  return new Promise((resolve, reject) => {
    pendingCuts.set(id, { resolve, reject, object });
    filterWorker!.postMessage({ kind: 'filter', id, key, bytes: copy, z, x, y } satisfies FilterRequest, [copy.buffer]);
  });
}
